import React, { useCallback, useMemo, useState } from "react";
import {
  DataTableCellContent,
  Text,
  createColumnHelper,
  type DataTableColumnDef,
} from "@salestrak/ui";
import { dataToField, fieldDto } from "@salestrak/custom-fields";
import type { IssueRow } from "#app/lib/data";
import { canEdit } from "#app/lib/utils/access-level";
import {
  CustomFieldValueCell,
} from "#app/components/custom-fields/custom-field-value-cell";
import { getCustomFieldDisplayValue } from "#app/components/custom-fields/custom-field-renderers";
import type { CustomFieldDef } from "#app/components/custom-fields/types";
import type { CommitCustomFieldValue } from "#app/hooks/issue-view/use-custom-field-value-commit";

/**
 * Custom-field columns for the shared {@link IssuesTable}: one opt-in column
 * per workspace field, whose cells view and edit the value through the same
 * {@link CustomFieldValueCell} the issue view dialog renders. The host keeps
 * them hidden by default (`useTableViewSettings` `defaultHidden`) — a
 * workspace can carry dozens of fields.
 */

const h = createColumnHelper<IssueRow>();

/** Column id prefix — stable across field renames, collision-free with the
 *  built-in column ids, and what the persisted view settings key on. */
export const ISSUE_CF_COLUMN_PREFIX = "cf:";

export function issueCustomFieldColumnId(customFieldId: string): string {
  return `${ISSUE_CF_COLUMN_PREFIX}${customFieldId}`;
}

function IssueCustomFieldCell({
  issue,
  field,
  commit,
}: {
  issue: IssueRow;
  field: CustomFieldDef;
  commit: CommitCustomFieldValue;
}) {
  const readOnly = !canEdit(issue.viewerAccessLevel);
  const [isActive, setIsActive] = useState(false);
  // Mirror of the cell's overlay/touch-reveal state; feeds `highlighted` so
  // the cell's own dismiss lifecycle works without a dialog-style container.
  const [overlayOpen, setOverlayOpen] = useState(false);

  const rawValue: unknown = issue.values?.[field.customFieldId];
  // Field-space conversion, exactly as the issue view's formValues does it.
  // An absent value stays `undefined` — the cell renders its empty marker.
  const value = useMemo(
    () =>
      rawValue === undefined
        ? undefined
        : dataToField(field.type, rawValue).fieldValue,
    [field.type, rawValue],
  );

  const handleUpdate = useCallback(
    (next: unknown) => {
      // fieldDto coerces rather than throws; commit surfaces its own errors.
      commit(
        issue,
        field.customFieldId,
        next === undefined ? undefined : fieldDto(field.type, next),
      );
    },
    [commit, issue, field],
  );

  const handleActivate = useCallback(() => setIsActive(true), []);
  const handleDeactivate = useCallback(() => setIsActive(false), []);

  // Photos stay display-only here: the upload grid grows row height, which
  // the windowed virtual scroll cannot take. Editing photos lives in the
  // issue view dialog.
  if (field.type === "photos") {
    const display = getCustomFieldDisplayValue(field, value);
    return (
      <DataTableCellContent>
        <Text
          size="sm"
          variant={display === "-" ? "muted" : undefined}
          truncate={1}
        >
          {display}
        </Text>
      </DataTableCellContent>
    );
  }

  return (
    <CustomFieldValueCell
      field={field}
      value={value}
      isActive={isActive}
      // A table cell hard-clips: the row-style end bleed would cut the
      // outline's corners square.
      bleedEnd={false}
      highlighted={overlayOpen || isActive}
      onUpdate={handleUpdate}
      onOverlayChange={setOverlayOpen}
      onActivate={handleActivate}
      onDeactivate={handleDeactivate}
      readOnly={readOnly}
      downloadPrefix={issue.name}
    />
  );
}

export function useIssueCustomFieldColumns({
  fields,
  commit,
}: {
  fields: CustomFieldDef[];
  commit: CommitCustomFieldValue;
}): DataTableColumnDef<IssueRow>[] {
  return useMemo(
    () =>
      fields.map((field) =>
        h.display({
          id: issueCustomFieldColumnId(field.customFieldId),
          // Plain string: the column settings panel and header-drag ghost
          // derive their labels from string headers.
          header: field.name,
          meta: { minWidth: 160, maxWidth: 320 },
          cell: ({ row }) => (
            <IssueCustomFieldCell
              issue={row.original}
              field={field}
              commit={commit}
            />
          ),
        }),
      ),
    [fields, commit],
  );
}
